/* eslint-disable max-lines */
import {
  BusinessHours,
  Situation,
  SituationColor,
  SOPNote,
  SOPTalkdownType,
  SOPWorkflow,
  UpdateSOPPayload,
} from '@hakimo-ui/hakimo/types';
import { Button, Label, Selectable, Textarea } from '@hakimo-ui/shared/ui-base';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  ExclamationTriangleIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { getPayloadFromSchedules } from '../schedule/siteSchedule';
import {
  getSchedulesFromTimeRange,
  normalizeSchedules,
} from '../sop-workflow/scheduleUtils';
import {
  DEFAULT_SOP_QUICK_RESOLVE,
  DEFAULT_SOP_SITUATIONS,
  SOPWorkflowFormItems,
} from './constants';

export const getTitleWithTimezone = (
  title: string,
  timezone?: string
): string => {
  if (!timezone) return title;
  return `${title} (${timezone})`;
};

const migrateNotesToSOPNotes = (notes: any): SOPNote[] => {
  if (!notes || !Array.isArray(notes)) return [];
  return notes.map((note: any) => {
    if (typeof note === 'string') {
      return {
        text: note,
        isRelevantForAiOperator: true,
        cameras: [],
      };
    }
    if (typeof note === 'object' && note.text !== undefined) {
      return {
        text: note.text,
        isRelevantForAiOperator: note.isRelevantForAiOperator ?? true,
        cameras: note.cameras ?? [],
        timeRange: note.timeRange,
      };
    }
    return {
      text: '',
      isRelevantForAiOperator: true,
      cameras: [],
    };
  });
};

const migrateSituations = (situations: any): Situation[] => {
  if (!situations || !Array.isArray(situations)) return [];
  return situations.map((situation: any): Situation => {
    if (typeof situation === 'object' && situation.label !== undefined) {
      const schedules = situation.schedules
        ? normalizeSchedules(situation)
        : getSchedulesFromTimeRange(situation.timeRange);
      return {
        label: situation.label,
        color: situation.color,
        subSituations: situation.subSituations ?? [],
        cameras: situation.cameras ?? [],
        schedules: schedules.length
          ? getPayloadFromSchedules(
              schedules,
              situation.schedules?.timezone ?? ''
            )
          : undefined,
      };
    }
    return {
      label: '',
      color: SituationColor.BLUE,
      subSituations: [],
      cameras: [],
    };
  });
};

const migrateExceptionsToSOPExceptions = (exceptions: any) => {
  if (!exceptions || !Array.isArray(exceptions)) return [];
  return exceptions.map((exception: any) => {
    if (typeof exception === 'string') {
      return {
        text: exception,
        isRelevantForAiOperator: true,
        cameras: [],
      };
    }
    if (typeof exception === 'object' && exception.text !== undefined) {
      return {
        text: exception.text,
        isRelevantForAiOperator: exception.isRelevantForAiOperator ?? true,
        cameras: exception.cameras ?? [],
        timeRange: exception.timeRange,
      };
    }
    return {
      text: '',
      isRelevantForAiOperator: true,
      cameras: [],
    };
  });
};

export const updateNullValues = (
  sopWorkflow: SOPWorkflow,
  timezone?: string
) => {
  const updatedSopWorkflow = {
    ...sopWorkflow,
    notes: migrateNotesToSOPNotes(sopWorkflow?.notes),
    situations: migrateSituations(sopWorkflow?.situations),
    exceptions: migrateExceptionsToSOPExceptions(sopWorkflow?.exceptions),
    escalationProtocol: sopWorkflow?.escalationProtocol ?? [],
    quickResolveActions: sopWorkflow?.quickResolveActions ?? [],
    talkdownNotes: sopWorkflow?.talkdownNotes ?? [],
    businessHours: sopWorkflow?.businessHours ?? { hours: '', timezone: '' },
    siteCategory: sopWorkflow?.siteCategory ?? '',
  };
  if (timezone && updatedSopWorkflow.businessHours) {
    updatedSopWorkflow.businessHours = {
      ...updatedSopWorkflow.businessHours,
      timezone: timezone,
    };
  }
  if (!sopWorkflow.talkdowns) {
    updatedSopWorkflow.talkdowns = [];
    sopWorkflow.firstTalkdown &&
      updatedSopWorkflow.talkdowns.push(sopWorkflow.firstTalkdown);
    sopWorkflow.secondTalkdown &&
      updatedSopWorkflow.talkdowns.push(sopWorkflow.secondTalkdown);
  }
  if (sopWorkflow.isZeroTolerance === undefined) {
    updatedSopWorkflow.isZeroTolerance = false;
  }
  if (sopWorkflow.isLongRangeAnalysisEnabled === undefined) {
    updatedSopWorkflow.isLongRangeAnalysisEnabled = false;
  }
  if (!sopWorkflow.situations || sopWorkflow.situations.length === 0) {
    updatedSopWorkflow.situations = DEFAULT_SOP_SITUATIONS;
  }
  if (
    !sopWorkflow.quickResolveActions ||
    sopWorkflow.quickResolveActions.length === 0
  ) {
    updatedSopWorkflow.quickResolveActions = DEFAULT_SOP_QUICK_RESOLVE;
  }
  return updatedSopWorkflow;
};

export const getFormData = (
  isEditing: boolean,
  editedData: SOPWorkflow,
  initData?: SOPWorkflow
) => {
  const isTalkdownEnabled = isEditing
    ? editedData.isTalkdownEnabled
    : initData?.isTalkdownEnabled;

  let filteredItems = SOPWorkflowFormItems;
  if (!isTalkdownEnabled) {
    filteredItems = filteredItems.filter(
      (item) => item.inputType !== 'talkdowns'
    );
  }

  return filteredItems;
};

export const getInitialData = (): SOPWorkflow => {
  return {
    exceptions: [{ text: '', isRelevantForAiOperator: true, cameras: [] }],
    siteAddress: '',
    businessHours: { hours: '', timezone: '' } as BusinessHours,
    siteGoogleMapLocation: '',
    siteCategory: '',
    siteDescription: '',
    escalationPoints: [''],
    isTalkdownEnabled: true,
    notes: [{ text: '', isRelevantForAiOperator: true, cameras: [] }],
    situations: [...DEFAULT_SOP_SITUATIONS],
    escalationProtocol: [''],
    quickResolveActions: [...DEFAULT_SOP_QUICK_RESOLVE],
    talkdowns: [
      {
        text: '',
        type: SOPTalkdownType.STATIC,
      },
    ],
    talkdownNotes: [''],
  };
};

export const prepareSOPPayload = (
  sopWorkflow: SOPWorkflow
): { sop_workflow: SOPWorkflow } => {
  // Create a copy to avoid mutating the original
  const updatedWorkflow = { ...sopWorkflow };

  // Remove firstTalkdown and secondTalkdown fields if talkdowns are available
  if (updatedWorkflow.talkdowns && updatedWorkflow.talkdowns.length > 0) {
    delete updatedWorkflow.firstTalkdown;
    delete updatedWorkflow.secondTalkdown;
  }

  const fallbackTimezone = updatedWorkflow.businessHours?.timezone || 'UTC';
  const situations = (updatedWorkflow.situations ?? []).map((situation) =>
    situation.schedules && !situation.schedules.timezone
      ? {
          ...situation,
          schedules: { ...situation.schedules, timezone: fallbackTimezone },
        }
      : situation
  );

  return {
    sop_workflow: { ...updatedWorkflow, situations },
  };
};

export const createUpdateSOPPayload = (
  sopWorkflow: SOPWorkflow,
  alarmTypeId?: string | null,
  clearAlarmType?: boolean
): UpdateSOPPayload => {
  const payload = prepareSOPPayload(sopWorkflow);

  return {
    sop_text: JSON.stringify(payload),
    alarm_type_id: alarmTypeId || undefined,
    clear_alarm_type: clearAlarmType,
  };
};

export const createCreateSOPPayload = (
  sopWorkflow: SOPWorkflow,
  tenantId: string,
  locationId: string,
  alarmTypeId?: string | null
) => {
  const payload = prepareSOPPayload(sopWorkflow);

  return {
    sop_text: JSON.stringify(payload),
    tenant_id: tenantId,
    location_id: locationId,
    alarm_type_id: alarmTypeId || undefined,
  };
};

// Utility functions for SOP components
export const displayAlarmTypeValue = (item?: Selectable) => {
  return item?.name || '';
};

export const filterAlarmTypesByQuery = (
  items: Selectable[],
  query: string
): Selectable[] => {
  if (!query) {
    return items;
  }
  return items.filter((item) =>
    item.name.toLowerCase().includes(query.toLowerCase())
  );
};

export const createAlarmTypeChangeHandler = (
  setSelectedAlarmType: (value: Selectable | undefined) => void,
  onChange?: (value: Selectable) => void
) => {
  return (value: Selectable) => {
    setSelectedAlarmType(value);
    onChange?.(value);
  };
};

export const createSOPAlarmTypeChangeHandler = (
  setSelectedAlarmType: (value: Selectable) => void,
  onChange: (alarmType: Selectable | null) => void
) => {
  return (value: Selectable | null) => {
    if (value) {
      setSelectedAlarmType(value);
      onChange(value.id === 'default' ? null : value);
    }
  };
};

// Business logic functions for SOP operations
export const createFilteredAlarmTypesGetter = (
  getAlarmTypesByName: (query: string) => Promise<Selectable[]>,
  sopAlarmTypes: Selectable[]
) => {
  return async (query: string): Promise<Selectable[]> => {
    try {
      const alarmTypes = await getAlarmTypesByName(query);
      if (sopAlarmTypes && sopAlarmTypes.length > 0) {
        const existingIds = sopAlarmTypes.map((type: Selectable) => type.id);
        return alarmTypes.filter(
          (type: Selectable) => !existingIds.includes(type.id)
        );
      }
      return alarmTypes;
    } catch (error) {
      return [];
    }
  };
};

export const createSOPPayloadFromDefaults = async (
  defaultSOPQuery: { refetch: () => Promise<any> },
  tenantId: string,
  locationId: string,
  alarmTypeId: string
) => {
  const result = await defaultSOPQuery.refetch();
  let sopWorkflow;

  if (result.isSuccess && result.data?.sop_workflow) {
    sopWorkflow = { ...result.data.sop_workflow };
  } else {
    sopWorkflow = updateNullValues(getInitialData());
  }

  return {
    sop_text: JSON.stringify(prepareSOPPayload(sopWorkflow)),
    tenant_id: tenantId,
    location_id: locationId,
    alarm_type_id: alarmTypeId,
  };
};

interface DeleteConfirmationProps {
  alarmType: Selectable;
  isLoading: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}
export const DeleteConfirmation = ({
  alarmType,
  isLoading,
  onConfirm,
  onCancel,
}: DeleteConfirmationProps) => (
  <div className="text-status-red dark:border-status-red/20 dark:bg-status-red/10 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm dark:text-red-200">
    <div className="flex items-start gap-3">
      <div className="flex-shrink-0">
        <ExclamationTriangleIcon className="h-5 w-5" />
      </div>
      <div className="flex-1">
        <div className="flex gap-2">
          <span className="font-bold">Confirm Deletion:</span>
          <span>Delete SOP for "{alarmType.name}"?</span>
        </div>
        <div className="mt-1 text-xs">This action cannot be undone.</div>
        <div className="mt-3 flex gap-2">
          <Button
            onClick={onConfirm}
            variant="error"
            disabled={isLoading}
            loading={isLoading}
          >
            {isLoading ? 'Deleting...' : 'Delete'}
          </Button>
          <Button onClick={onCancel} variant="outline" disabled={isLoading}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  </div>
);

export const createInlineDeleteHandlers = (
  setDeleteConfirmation: (alarmType: Selectable | null) => void,
  deleteMutation: { mutate: (data: null) => void; isLoading: boolean },
  sopQuery: { data?: { id?: string } }
) => ({
  handleDeleteRequest: (alarmType: Selectable) => {
    setDeleteConfirmation(alarmType);
  },
  handleConfirmDelete: () => {
    if (sopQuery.data?.id) {
      deleteMutation.mutate(null);
    }
  },
  handleCancelDelete: () => {
    setDeleteConfirmation(null);
  },
});

interface TalkdownNotesProps {
  notes: string[];
}
export const TalkdownNotes = ({ notes }: TalkdownNotesProps) => {
  if (!notes || notes.length === 0) return null;
  return (
    <div className="py-2">
      <div className="text-primary-800 dark:text-primary-400 mb-1 font-sans font-semibold">
        Talkdown Notes
      </div>
      <ul className="list-disc space-y-2">
        {notes.map((note, i) => (
          <li key={i} className="ml-4 list-item [overflow-wrap:anywhere]">
            {note}
          </li>
        ))}
      </ul>
    </div>
  );
};

export const getInfoTextByFieldId = (fieldId: string): string | undefined => {
  const item = SOPWorkflowFormItems.find((formItem) => formItem.id === fieldId);
  return item?.info;
};

export const renderEditableList = (
  items: string[],
  onItemChange: (index: number, value: string) => void,
  onItemDelete: (index: number) => void,
  placeholder = 'Enter item...'
) => (
  <div className="space-y-3">
    {items.map((item, index) => (
      <div key={index} className="flex items-center gap-2">
        <div className="flex-1">
          <Textarea
            value={item}
            onChange={(value) => onItemChange(index, value)}
            placeholder={placeholder}
            rows={3}
          />
        </div>
        <Button
          variant="icon"
          onClick={() => onItemDelete(index)}
          title="Delete item"
        >
          <TrashIcon className="h-5 w-5" />
        </Button>
      </div>
    ))}
  </div>
);

export const renderViewableList = (items: string[], spacing = 'space-y-2') =>
  items && items.length > 0 ? (
    <ol className={`list-inside list-decimal ${spacing}`}>
      {items.map((item, i) => (
        <li key={i} className="text-onlight-text-2 dark:text-ondark-text-2">
          <span className="break-words">{item}</span>
        </li>
      ))}
    </ol>
  ) : (
    <div className="text-onlight-text-3 dark:text-ondark-text-3">
      Not applicable
    </div>
  );

export const renderEmptyState = () => (
  <div className="text-onlight-text-3 dark:text-ondark-text-3">
    Not applicable
  </div>
);

export const renderSubItems = (
  subItems: string[],
  expandedIndex: boolean | undefined,
  onToggleExpanded: () => void
) => {
  if (!subItems || subItems.length === 0) return null;

  return (
    <div className="ml-2 space-y-1">
      <div className="flex flex-wrap gap-1 border-l-2 border-current pl-2 opacity-70">
        {subItems
          .slice(0, expandedIndex || subItems.length <= 2 ? subItems.length : 2)
          .map((subItem: string, subIdx: number) => (
            <Label key={subIdx} small text={subItem} />
          ))}
      </div>
      {subItems.length > 2 && (
        <button
          onClick={onToggleExpanded}
          className="text-onlight-text-3 hover:text-onlight-text-1 dark:text-ondark-text-3 dark:hover:text-ondark-text-1 ml-2 flex items-center gap-1 text-xs transition-colors"
        >
          {expandedIndex ? (
            <>
              <ChevronUpIcon className="h-3 w-3" />
              Show less
            </>
          ) : (
            <>
              <ChevronDownIcon className="h-3 w-3" />
              Show {subItems.length - 2} more
            </>
          )}
        </button>
      )}
    </div>
  );
};
